import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { getLandingQualificationWizard, submitLandingQualificationWizard, trackMarketingLiveEvent } from '../../services/searchConsoleApi';

const initialRemote = { loading: true, error: '', data: null };

function wizardMarketingSession() {
  try {
    const key = 'bbts_marketing_session';
    let value = window.sessionStorage.getItem(key);
    if (!value) {
      value = `${Date.now().toString(36)}_${crypto.getRandomValues(new Uint32Array(2)).join('')}`;
      window.sessionStorage.setItem(key, value);
    }
    return value;
  } catch (_) { return `bbts_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`; }
}

export default function LandingQualificationWizard() {
  const { slug } = useParams();
  const [remote, setRemote] = useState(initialRemote);
  const [contact, setContact] = useState({ name: '', phone: '', email: '', businessName: '' });
  const [phase, setPhase] = useState('contact');
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [visitorSession] = useState(wizardMarketingSession);

  const attribution = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const take = (name) => params.get(name) || '';
    return {
      referrer: document.referrer || '',
      utm_source: take('utm_source'), utm_medium: take('utm_medium'),
      utm_campaign: take('utm_campaign'), utm_term: take('utm_term'),
      utm_content: take('utm_content'), gclid: take('gclid'), fbclid: take('fbclid'),
      campaign_id: take('campaign_id'), token: take('token'),
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (typeof window.fbq === 'function') window.fbq('trackCustom', 'BBTSWizardStarted', { landing_slug: slug });
    trackMarketingLiveEvent({
      eventType: 'wizard_started', slug, sessionId: visitorSession, attribution,
      language: navigator.language || '', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      viewport: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
    }).catch(() => undefined);
    getLandingQualificationWizard(slug)
      .then((data) => active && setRemote({ loading: false, error: '', data }))
      .catch((error) => active && setRemote({ loading: false, error: error.message || 'No se pudo abrir este cuestionario.', data: null }));
    return () => { active = false; };
  }, [slug, visitorSession, attribution]);

  // Una landing de producto no debe volver a preguntar algo que ya conoce.
  // El CTA de Meso 360 abre directamente el cuestionario de restaurante;
  // se conserva el mismo scoring y atribución que el wizard general.
  const fixedProductLine = remote.data?.landing?.wizardType === 'meso360' ? 'restaurante' :
    remote.data?.landing?.wizardType === 'medic360' ? 'salud' :
      remote.data?.landing?.wizardType === 'master360' ? 'erp' : '';

  useEffect(() => {
    if (!fixedProductLine) return;
    setAnswers((current) => current.productLine === fixedProductLine ? current : { productLine: fixedProductLine });
    setStep(0);
  }, [fixedProductLine]);

  const steps = useMemo(() => {
    const questions = remote.data?.questions;
    if (!questions) return [];
    if (fixedProductLine) return questions[fixedProductLine] || [];
    return [questions.productLine, ...(answers.productLine ? questions[answers.productLine] || [] : [])];
  }, [remote.data, answers.productLine, fixedProductLine]);
  const current = steps[step];
  const validContact = Boolean(contact.name.trim() && contact.phone.trim());
  const canContinue = current && (current.multi ? Array.isArray(answers[current.id]) && answers[current.id].length : Boolean(answers[current.id]));

  const choose = (question, value) => {
    if (question.id === 'productLine' && answers.productLine !== value) setStep(0);
    setAnswers((previous) => {
      if (!question.multi) {
        const next = { ...previous, [question.id]: value };
        if (question.id === 'productLine' && previous.productLine !== value) Object.keys(next).forEach((key) => { if (key !== 'productLine') delete next[key]; });
        return next;
      }
      const selected = new Set(previous[question.id] || []);
      selected.has(value) ? selected.delete(value) : selected.add(value);
      return { ...previous, [question.id]: [...selected] };
    });
  };

  const next = async () => {
    if (phase === 'contact') {
      if (validContact) setPhase('questions');
      return;
    }
    if (!canContinue) return;
    if (step < steps.length - 1) { setStep((value) => value + 1); return; }
    setSubmitting(true); setRemote((value) => ({ ...value, error: '' }));
    try {
      await submitLandingQualificationWizard({ slug, contact, answers, attribution });
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: `landing:${slug}`, content_category: answers.productLine || 'general' });
        window.fbq('trackCustom', 'BBTSWizardCompleted', { landing_slug: slug, solution_id: answers.productLine || 'general' });
      }
      trackMarketingLiveEvent({
        eventType: 'wizard_completed', slug, sessionId: visitorSession, attribution,
        language: navigator.language || '', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
        viewport: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
      }).catch(() => undefined);
      setCompleted(true);
    } catch (error) {
      setRemote((value) => ({ ...value, error: error.message || 'No se pudieron guardar tus respuestas.' }));
    } finally { setSubmitting(false); }
  };

  if (remote.loading) return <PageShell><p className="text-center text-slate-500">Preparando tus preguntas…</p></PageShell>;
  if (remote.error && !remote.data) return <PageShell><p className="text-center text-sm text-red-700">{remote.error}</p></PageShell>;
  if (completed) return <PageShell><div className="text-center"><CheckCircleIcon className="mx-auto h-14 w-14 text-emerald-500" /><h1 className="mt-4 text-2xl font-black text-slate-900">¡Gracias por contarnos!</h1><p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-slate-600">Recibimos tu información. Un asesor de BBTS revisará lo que necesitas y se comunicará contigo.</p></div></PageShell>;

  const total = phase === 'contact' ? steps.length + 1 : steps.length + 1;
  const position = phase === 'contact' ? 1 : step + 2;
  return <PageShell>
    <p className="text-center text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">BBTS</p>
    <h1 className="mt-2 text-center text-2xl font-black text-slate-900">{remote.data?.landing?.title || 'Cuéntanos qué necesitas'}</h1>
    <p className="mx-auto mt-2 max-w-md text-center text-sm leading-6 text-slate-600">{fixedProductLine ? 'Cuéntanos brevemente sobre tu restaurante para recomendarte el siguiente paso correcto.' : 'Tomará menos de dos minutos. Con esta información preparamos el seguimiento correcto, sin agendarte automáticamente.'}</p>
    {remote.error && <p role="alert" className="mt-5 rounded-lg bg-red-50 p-3 text-sm text-red-700">{remote.error}</p>}
    <div className="mt-7"><div className="mb-3 flex items-center justify-between text-xs font-semibold text-slate-500"><span>Paso {position} de {total}</span><span>{Math.round((position / total) * 100)}%</span></div><div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${(position / total) * 100}%` }} /></div></div>
    {phase === 'contact' ? <section className="mt-7"><h2 className="text-lg font-bold text-slate-900">¿Cómo podemos contactarte?</h2><p className="mt-1 text-sm text-slate-500">Tu teléfono permite que el equipo dé seguimiento a esta solicitud.</p><div className="mt-4 grid gap-3"><Field label="Nombre *" value={contact.name} onChange={(value) => setContact((item) => ({ ...item, name: value }))} /><Field label="Teléfono *" type="tel" value={contact.phone} onChange={(value) => setContact((item) => ({ ...item, phone: value }))} /><Field label="Email" type="email" value={contact.email} onChange={(value) => setContact((item) => ({ ...item, email: value }))} /><Field label="Negocio (opcional)" value={contact.businessName} onChange={(value) => setContact((item) => ({ ...item, businessName: value }))} /></div></section> : current && <section className="mt-7"><h2 className="text-lg font-bold text-slate-900">{current.question}</h2>{current.multi && <p className="mt-1 text-xs text-slate-500">Puedes seleccionar más de una opción.</p>}<div className="mt-4 space-y-3">{current.options.map((option) => { const selected = current.multi ? (answers[current.id] || []).includes(option.value) : answers[current.id] === option.value; return <button type="button" key={option.value} onClick={() => choose(current, option.value)} className={`w-full rounded-xl border px-4 py-3 text-left text-sm font-semibold transition ${selected ? 'border-emerald-500 bg-emerald-50 text-emerald-900 ring-1 ring-emerald-500' : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300'}`}><span className={`mr-3 inline-flex h-5 w-5 items-center justify-center border ${current.multi ? 'rounded' : 'rounded-full'} ${selected ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300'}`}>{selected ? '✓' : ''}</span>{option.label}</button>; })}</div></section>}
    <div className="mt-8 flex gap-3"><button type="button" onClick={() => phase === 'questions' && step === 0 ? setPhase('contact') : setStep((value) => Math.max(0, value - 1))} disabled={phase === 'contact' || submitting} className="rounded-lg border border-slate-200 px-4 py-3 text-sm font-bold text-slate-600 disabled:opacity-40">Atrás</button><button type="button" onClick={next} disabled={(phase === 'contact' ? !validContact : !canContinue) || submitting} className="flex-1 rounded-lg bg-emerald-600 px-4 py-3 text-sm font-bold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-40">{submitting ? 'Guardando…' : phase === 'contact' || step < steps.length - 1 ? 'Continuar' : 'Enviar respuestas'}</button></div>
  </PageShell>;
}

function Field({ label, value, onChange, type = 'text' }) { return <label className="text-sm font-semibold text-slate-700">{label}<input type={type} value={value} onChange={(event) => onChange(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-3 font-normal outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500" /></label>; }
function PageShell({ children }) { return <main className="min-h-screen bg-slate-50 px-4 py-12 sm:py-20"><div className="mx-auto max-w-xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-9">{children}</div></main>; }
